import React, {useCallback, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {safeCaptureException} from 'utils/sentry';
import {
  BiomatchResponse,
  CompareBiomatchResponse,
  FieldsErrorResponse,
  Guest,
  GUEST_STATUSES,
  IMAGE_SERVICE_ORIGIN,
  OCRDetectedData,
  IV_DOCUMENT_IMAGE_TYPES,
} from '@guestapp/sdk';
import useBuildOCRDetectedData from './useBuildOCRDetectedData';
import ChekinPro from 'ChekinPro';
import {PATHS} from '../Routes';
import {ANALYTIC_EVENTS, Analytics} from '../analytics';
import {STATUSES, STORAGE_KEYS} from '../utils/constants';
import {getErrorMessage, isObject, SessionStorage} from '@guestapp/core';
import {
  IVCheckProps,
  SendSelfieAndDocumentToCompareOptions,
  useBiomatch,
  useBuildIVPayload,
  useEvent,
  useGuest,
  useGuestMutation,
  useOCR,
  usePath,
} from './';
import {
  useIVStore,
  getDocumentNationality,
  getDocumentType,
} from 'store/identityVerification';
import {useGetGuestId} from './useGetGuestId';
import {useSummary} from 'context/summary';
import {useErrorModal, useLockScrollBody, useModalControls} from './common';
import {useActions} from './redux';
import {useIVComputedDetails} from './useIVComputedDetails';
import {useGetNextStepAfterIV} from './useGetNextStepAfterIV';
import {useVerificationCompleted} from 'components/VerificationCompletedModal';
import {useUpdateIvIntent} from 'hooks/iv/useUpdateIvIntent';
import {useS3Upload} from './s3Upload/useS3Upload';
import {useScannerControls} from 'context/kiosko/hooks/useScannerControls';
import {getIVIntentRemoteSession} from 'hooks/iv/useIVRemoteSync';
import {SDK_MODE} from 'utils/embedded';
import ChekinProSettings from '../ChekinProSettings';
import IVRetryModal from '../components/IVRetryModal';
import IVProcessModal from '../components/IVProcessModal';
import Button from '../components/common/Button';
import {ModalTwoButtonsWrapper} from 'styled/common';

const IVSuccessTimeout = 2500;

type UseUpdateGuestIVData = {
  onSuccess: (data: Guest) => void;
  onError: (error: FieldsErrorResponse) => void;
};
function useUpdateGuestIVData(props: UseUpdateGuestIVData) {
  const onSuccess = useEvent(props.onSuccess);
  const onError = useEvent(props.onError);
  const mutate = useGuestMutation({
    onSuccess,
    onError,
  });
  const {getIVPayload} = useBuildIVPayload();

  const updateGuest = useEvent((id: string) => {
    const payload = getIVPayload();
    mutate.mutate({
      id,
      payload,
    });
  });

  return {updateGuest};
}

type UseDocumentSelfieDetection = {
  docType?: IV_DOCUMENT_IMAGE_TYPES;
  onlyScanning?: boolean;
  onError?: (error: Error | string) => void;
  livenessVideo?: Blob | null;
};
function useDocumentSelfieDetection({
  onError,
  onlyScanning,
  docType,
  livenessVideo,
}: UseDocumentSelfieDetection = {}) {
  const {sendUpdateIvIntentRequestAsync} = useUpdateIvIntent();
  const {openModal: openIVCompletedModal} = useVerificationCompleted();
  const navigate = useNavigate();
  const {resetPhotoBoxUploadedImage} = useActions();
  const {
    setDistanceBiomatchComparing,
    setOCRDocumentPassed,
    resetIVConfigState,
    setBiomatchPassed,
    setImageDetails,
    isRemoteSession,
    imagesDetails,
  } = useIVStore();
  const {t} = useTranslation();
  const id = useGetGuestId();
  const {guest} = useGuest({id});
  const needToUpdateGuest =
    id && guest?.guestapp_status === GUEST_STATUSES.verification_pending;

  const {biomatch} = useIVComputedDetails();
  const {reservationId, countryCode} = useSummary();
  const {buildFormData} = useBuildOCRDetectedData();
  const documentType = useIVStore(getDocumentType);
  const nationality = useIVStore(getDocumentNationality);
  const frontSideImage = useIVStore(state => state.imagesDetails.front);
  const livenessResultData = useIVStore(state => state.livenessResultData);
  const {getMainPath} = usePath();
  const {resetScroll} = useLockScrollBody();
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
  const handlerRef = React.useRef<null | (() => void)>(null);
  const sendSelfieAndDocumentToCompareRef = React.useRef<
    null | ((args: SendSelfieAndDocumentToCompareOptions) => void)
  >(null);
  const {ErrorModal: DefaultErrorModal, closeErrorModal, displayError} = useErrorModal();
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [processState, setProcessState] = React.useState<STATUSES>(STATUSES.loading);
  const handleMRZFailedRef = React.useRef<
    (errorMessage: string, data: OCRDetectedData & {image: string}) => void
  >(() => {});
  const isBiomatchAndPassport = biomatch && !documentType?.two_sides;
  const {
    isOpen: isProcessModalOpen,
    openModal: openProcessModal,
    closeModal: closeProcessModal,
  } = useModalControls();
  const {
    isOpen: isRetryModalOpen,
    openModal: openRetryModal,
    closeModal: closeRetryModal,
  } = useModalControls();

  const {isScanning} = useScannerControls();
  const {getNextStepDetails} = useGetNextStepAfterIV({docType, onlyScanning});

  useEffect(() => {
    if (isScanning) {
      openProcessModal();
    } else {
      closeProcessModal();
    }
  }, [isScanning, openProcessModal, closeProcessModal]);

  const handleBiomatchRetriesFinish = React.useCallback(
    (data: CompareBiomatchResponse | BiomatchResponse) => {
      Analytics.track(ANALYTIC_EVENTS.ivRetryingLimitOver, {
        check_id: data.id,
        status: data.status,
        picture_file: (data as BiomatchResponse).picture_file,
        is_match: Boolean((data as CompareBiomatchResponse).is_match),
        type: 'Biomatch',
      });
      openRetryModal();
    },
    [openRetryModal],
  );

  const handleOCRRetriesFinish = React.useCallback(
    (data: OCRDetectedData) => {
      Analytics.track(ANALYTIC_EVENTS.ivRetryingLimitOver, {
        check_id: data.id,
        status: data.status,
        picture_file: data.picture_file,
        mrz_detected: Boolean(data.mrz_detected),
        barcode_detected: Boolean(data.barcode_detected),
        type: 'OCR',
      });
      openRetryModal();
    },
    [openRetryModal],
  );

  const onBefore = React.useCallback(() => {
    setProcessState(STATUSES.loading);
    openProcessModal();
  }, [openProcessModal]);

  React.useEffect(() => {
    const timeout = timeoutRef.current;

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const closeAndCleanLoadingModal = React.useCallback(() => {
    closeProcessModal();
    setProcessState(STATUSES.idle);
  }, [closeProcessModal]);

  React.useEffect(() => {
    return () => {
      closeAndCleanLoadingModal();
      resetScroll();
    };
  }, [closeAndCleanLoadingModal, resetScroll]);

  const handleError = React.useCallback(
    (error: Error | FieldsErrorResponse) => {
      Analytics.track(ANALYTIC_EVENTS.ivError, {
        'Error message': getErrorMessage(error),
      });
      closeAndCleanLoadingModal();
      setProcessState(STATUSES.idle);
      displayError(error);
      if (isObject(error) && 'errors' in error) {
        onError?.(error.errors?.[0]?.message);
      } else {
        onError?.(error as Error);
      }
    },
    [closeAndCleanLoadingModal, displayError, onError],
  );

  const handleFinish = React.useCallback(
    (to?: string) => {
      setProcessState(STATUSES.success);
      const nextStep = getNextStepDetails();
      if (nextStep.completed) {
        Analytics.track(ANALYTIC_EVENTS.ivFinishingAllSuccessfully);
      }

      const link = to || nextStep.path;
      const postHandler = () => {
        if (link) navigate(link);
      };

      timeoutRef.current = setTimeout(() => {
        if (ChekinProSettings.isSDK && nextStep.completed) {
          const state = useIVStore.getState();
          ChekinPro.events.onIVFinished({
            imagesDetails: state.imagesDetails,
            ...state.ocrResultData,
            ...state.biomatchResultData,
          });
          if (ChekinProSettings.mode === SDK_MODE.onlyIV) return;
        }

        if (
          isRemoteSession &&
          (link.endsWith(PATHS.addPersonalDataForm) ||
            link.endsWith(PATHS.guestRegistration))
        ) {
          openIVCompletedModal(postHandler);
        } else {
          postHandler();
        }
      }, IVSuccessTimeout);
    },
    [getNextStepDetails, navigate, isRemoteSession, openIVCompletedModal],
  );

  const {updateGuest} = useUpdateGuestIVData({
    onError: handleError,
    onSuccess: data => {
      if (data.guestapp_status === GUEST_STATUSES.complete) {
        SessionStorage.remove(STORAGE_KEYS.personalFormData);
        resetIVConfigState();
        handleFinish(getMainPath(PATHS.guestRegistration));
      } else {
        handleFinish();
      }
    },
  });

  const handleOCRData = React.useCallback(
    async (data: OCRDetectedData) => {
      if (!onlyScanning) {
        const intentId = getIVIntentRemoteSession();
        if (intentId) {
          await sendUpdateIvIntentRequestAsync({
            id: intentId,
            document_passed: true,
          });
        }
        setOCRDocumentPassed(data?.mrz_detected || data?.barcode_detected);
      }
      const builtFormData = buildFormData(data);
      SessionStorage.set(STORAGE_KEYS.personalFormData, builtFormData);
    },
    [onlyScanning, buildFormData, setOCRDocumentPassed, sendUpdateIvIntentRequestAsync],
  );

  const handleOCRSuccess = React.useCallback(
    async (image: string, data?: OCRDetectedData) => {
      if (data) await handleOCRData(data);

      if (!isBiomatchAndPassport && !onlyScanning && docType) {
        const docTypeImageDetails = {
          image,
          type: docType,
          mrzDetected: data?.mrz_detected,
          barcodeDetected: data?.barcode_detected,
          id: data?.id,
          service: IMAGE_SERVICE_ORIGIN.ocr,
          uploaded: true,
        };
        setImageDetails(docTypeImageDetails);
        const intentId = getIVIntentRemoteSession();

        if (intentId) {
          await sendUpdateIvIntentRequestAsync({
            id: intentId,
            image_details: {
              ...imagesDetails,
              [docType]: docTypeImageDetails,
            },
          });
        }

        if (docType === IV_DOCUMENT_IMAGE_TYPES.front) {
          Analytics.track(ANALYTIC_EVENTS.ivFinishingFrontSideDocument);
        } else if (docType === IV_DOCUMENT_IMAGE_TYPES.back) {
          Analytics.track(ANALYTIC_EVENTS.ivFinishingBackSideDocument);
        }
      }

      if (onlyScanning) {
        Analytics.track(ANALYTIC_EVENTS.ivFinishingDocumentMRZScan);
      }

      if (needToUpdateGuest) {
        setTimeout(() => updateGuest(id), 0);
        return;
      }

      handleFinish();
    },
    [
      imagesDetails,
      sendUpdateIvIntentRequestAsync,
      docType,
      handleFinish,
      handleOCRData,
      id,
      isBiomatchAndPassport,
      needToUpdateGuest,
      onlyScanning,
      setImageDetails,
      updateGuest,
    ],
  );

  const handleFailed = React.useCallback(
    (errorMessage?: string) => {
      if (docType === IV_DOCUMENT_IMAGE_TYPES.front) {
        setImageDetails({type: docType, id: '', service: IMAGE_SERVICE_ORIGIN.biomatch});
      }
      if (docType === IV_DOCUMENT_IMAGE_TYPES.back) {
        setImageDetails({type: docType, id: ''});
      }
      Analytics.track(ANALYTIC_EVENTS.ivError, {
        'Error message': errorMessage ?? '',
        'Image type': docType,
      });
      setErrorMessage(errorMessage);
      setProcessState(STATUSES.error);
    },
    [docType, setImageDetails],
  );

  const setLivenessResultData = useIVStore(state => state.setLivenessResultData);
  const {uploadFile} = useS3Upload();
  const onFinishUpload = useCallback(
    (value: string) => {
      setLivenessResultData({
        ...livenessResultData,
        livenessVideoUrl: value,
      });
    },
    [livenessResultData, setLivenessResultData],
  );

  const uploadLivenessVideo = useCallback(
    async (recordingVideo?: Blob | null) => {
      if (!recordingVideo) return;
      try {
        const videoFile = new File([recordingVideo], 'liveness-video.mp4', {
          type: 'video/mp4',
        });

        const {data, error} = await uploadFile(videoFile, {
          contentType: 'video/mp4',
        });

        if (data) {
          onFinishUpload(data.key);
          return;
        }

        if (error) {
          console.error(error);
          onFinishUpload('ERROR_UPLOADING_VIDEO');
          return;
        }

        return;
      } catch (error) {
        safeCaptureException(error, 'Document selfie detection error');
        console.error('Document selfie detection error:', error);
        return;
      }
    },
    [uploadFile, onFinishUpload],
  );

  const handleSelfieAndDocumentCompareSuccess = React.useCallback(
    async (data: CompareBiomatchResponse) => {
      const intentId = getIVIntentRemoteSession();
      if (intentId) {
        await sendUpdateIvIntentRequestAsync({
          id: intentId,
          biomatch_passed: true,
        });
      }

      setBiomatchPassed(data.is_match);
      const distance = Number(data.distance?.toFixed(1) ?? 0.0);
      setDistanceBiomatchComparing(distance);

      if (livenessVideo) {
        await uploadLivenessVideo(livenessVideo);
      }
      Analytics.track(ANALYTIC_EVENTS.ivFinishingMatching);
      if (needToUpdateGuest) {
        setTimeout(() => updateGuest(id), 0);
        return;
      }
      handleFinish();
    },
    [
      handleFinish,
      id,
      needToUpdateGuest,
      sendUpdateIvIntentRequestAsync,
      setBiomatchPassed,
      setDistanceBiomatchComparing,
      updateGuest,
      livenessVideo,
      uploadLivenessVideo,
    ],
  );

  const {sendAndVerifyImageForOCR} = useOCR({
    onRetriesFinish: handleOCRRetriesFinish,
    onDetectionFailed: (errorMessage, data) =>
      handleMRZFailedRef.current(errorMessage, data),
    onFinish: handleOCRSuccess,
    onError: handleError,
    onBefore,
  });

  handleMRZFailedRef.current = React.useCallback(
    (errorMessage: string, data: OCRDetectedData & {image: string}) => {
      if (documentType?.without_mrz) {
        handleOCRSuccess(data.image, {...(data || {}), mrz_detected: true});
        return;
      }
      if (
        documentType?.both_sides_mrz &&
        frontSideImage.image &&
        docType === IV_DOCUMENT_IMAGE_TYPES.back
      ) {
        void sendAndVerifyImageForOCR({
          documentType: documentType.value,
          nationality: documentType.value,
          image: frontSideImage.image,
          source: data.source,
          countryCode,
          reservationId,
        });
        return;
      }
      handleFailed(errorMessage);
    },
    [
      handleFailed,
      handleOCRSuccess,
      sendAndVerifyImageForOCR,
      reservationId,
      docType,
      documentType,
      frontSideImage.image,
      countryCode,
    ],
  );

  const handleBiomatchSuccess = React.useCallback(
    async (image: string, data: BiomatchResponse = {} as BiomatchResponse) => {
      const intentId = getIVIntentRemoteSession();
      if (docType === IV_DOCUMENT_IMAGE_TYPES.selfie) {
        const docTypeImageDetails = {
          image,
          type: docType,
          faceDetected: data?.is_face_detected,
          id: data?.id,
          service: IMAGE_SERVICE_ORIGIN.biomatch,
          uploaded: true,
        };
        setImageDetails(docTypeImageDetails);
        if (intentId) {
          await sendUpdateIvIntentRequestAsync({
            id: intentId,
            image_details: {
              ...imagesDetails,
              [docType]: docTypeImageDetails,
            },
          });
        }
        Analytics.track(ANALYTIC_EVENTS.ivFinishingSelfie);
        sendSelfieAndDocumentToCompareRef.current?.({
          reservationId,
          documentPhotoId: frontSideImage.id || '',
          personPhotoId: data?.id || '',
        });
        return;
      }
      if (docType) {
        const docTypeImageDetails = {
          image,
          type: docType,
          faceDetected: data?.is_face_detected,
          id: data?.id,
          service: IMAGE_SERVICE_ORIGIN.biomatch,
          uploaded: true,
        };

        setImageDetails(docTypeImageDetails);
        if (intentId) {
          await sendUpdateIvIntentRequestAsync({
            id: intentId,
            image_details: {...imagesDetails, [docType]: docTypeImageDetails},
          });
        }
        Analytics.track(ANALYTIC_EVENTS.ivFinishingDocumentFace, {
          side: docType,
        });
      }

      if (isBiomatchAndPassport && !documentType?.without_mrz) {
        void sendAndVerifyImageForOCR({
          documentType: documentType?.value,
          nationality: nationality,
          source: data.source,
          countryCode: 'ES',
          reservationId,
          image,
        });
      } else {
        handleFinish();
      }
    },
    [
      imagesDetails,
      sendUpdateIvIntentRequestAsync,
      docType,
      isBiomatchAndPassport,
      setImageDetails,
      reservationId,
      frontSideImage.id,
      sendAndVerifyImageForOCR,
      documentType?.value,
      documentType?.without_mrz,
      nationality,
      handleFinish,
    ],
  );

  const {
    sendAndVerifySelfieForFaceDetection,
    sendSelfieAndDocumentToCompare,
    sendAndVerifyDocumentForFaceDetection,
  } = useBiomatch({
    onSuccessComparing: handleSelfieAndDocumentCompareSuccess,
    onRetriesFinish: handleBiomatchRetriesFinish,
    onDetectionFailed: handleFailed,
    onFinish: handleBiomatchSuccess,
    onError: handleError,
    onBefore,
  });

  const handleRetryAgain = React.useCallback(() => {
    handlerRef.current?.();
    closeRetryModal();
  }, [closeRetryModal]);

  const handleCancelRetryAgain = React.useCallback(() => {
    closeAndCleanLoadingModal();
    setProcessState(STATUSES.idle);
    resetPhotoBoxUploadedImage();
    closeRetryModal();
    handlerRef.current = null;
  }, [closeAndCleanLoadingModal, closeRetryModal, resetPhotoBoxUploadedImage]);

  const ProcessModal = React.useCallback(() => {
    const reasons =
      docType === IV_DOCUMENT_IMAGE_TYPES.selfie
        ? [
            t('iv_failed_possible_reasons.covered_accessory'),
            t('iv_failed_possible_reasons.poor_or_side_lighting'),
          ]
        : [
            t('iv_failed_possible_reasons.the_document_is_cropped'),
            t('iv_failed_possible_reasons.the_photo_is_of_poor_quality'),
            t('iv_failed_possible_reasons.there_are_reflections'),
          ];

    return (
      <IVProcessModal
        open={isProcessModalOpen}
        errorMessage={errorMessage}
        reasons={reasons}
        state={processState}
        onTryAgain={closeAndCleanLoadingModal}
        docType={docType}
      />
    );
  }, [
    closeAndCleanLoadingModal,
    docType,
    errorMessage,
    isProcessModalOpen,
    processState,
    t,
  ]);

  const RetryModal = React.useCallback(() => {
    return (
      <IVRetryModal
        open={isRetryModalOpen}
        onClickTryAgain={handleRetryAgain}
        onCancel={handleCancelRetryAgain}
      />
    );
  }, [handleRetryAgain, isRetryModalOpen, handleCancelRetryAgain]);

  const ErrorModal = React.useCallback(() => {
    // const closeModalButtonMessage = isOnline ? 'try_again' : 'ok';
    return (
      <DefaultErrorModal
        message={t('iv_error_message')}
        description={t('iv_error_description')}
      >
        <ModalTwoButtonsWrapper>
          <Button
            label={t('try_again')}
            // disabled={!handlerRef.current}
            onClick={() => {
              closeErrorModal();
              // handlerRef.current?.();
            }}
          />
          <Button
            label={t('continue_the_registration')}
            onClick={() => navigate(getMainPath(PATHS.addPersonalDataForm))}
            link
          />
        </ModalTwoButtonsWrapper>
      </DefaultErrorModal>
    );
  }, [DefaultErrorModal, getMainPath, navigate, closeErrorModal, t]);

  const sendAndVerifyDocumentToBiomatchAndOCR = React.useCallback(
    async (props: IVCheckProps) => {
      void sendAndVerifyDocumentForFaceDetection(props);

      // const response = await sendToOCR(image, countryCode, reservationId);
      // if (response.id) {
      //   setImageDetails({type: IV_DOCUMENT_IMAGE_TYPES.front, extraID: response.id});
      // }
    },
    [sendAndVerifyDocumentForFaceDetection],
  );

  const sendSelfieAndDocumentToCompareAndGetOCRData = React.useCallback(
    async (data: SendSelfieAndDocumentToCompareOptions) => {
      //OCR WITH SELFIE HERE
      void sendSelfieAndDocumentToCompare(data);
      // if (frontSideImage.extraID) {
      //   const result = await checkOCRResult(frontSideImage.extraID);
      //   if (result) handleOCRData(result);
      // }
    },
    [sendSelfieAndDocumentToCompare],
  );

  const sendFrontSideDocument =
    biomatch || (!biomatch && documentType?.two_sides)
      ? sendAndVerifyDocumentToBiomatchAndOCR
      : sendAndVerifyImageForOCR;

  const sendBackSideDocument = sendAndVerifyImageForOCR;

  const proxyWrapper = React.useCallback(
    <T extends (...args: Parameters<T>) => void>(target: T) => {
      return (...args: Parameters<T>) => {
        const handlerFunc = () => target(...args);
        handlerRef.current = handlerFunc;
        return handlerFunc();
      };
    },
    [],
  );

  const sendFrontSideDocumentWrapped = proxyWrapper(sendFrontSideDocument);
  const sendBackSideDocumentWrapped = proxyWrapper(sendBackSideDocument);
  const sendAndReadImageForOCRWrapped = proxyWrapper(sendAndVerifyImageForOCR);
  const sendAndVerifySelfieForFaceDetectionWrapped = proxyWrapper(
    sendAndVerifySelfieForFaceDetection,
  );
  const sendSelfieAndDocumentToCompareWrapped = proxyWrapper(
    sendSelfieAndDocumentToCompareAndGetOCRData,
  );

  React.useEffect(() => {
    if (!sendSelfieAndDocumentToCompareWrapped) return;
    sendSelfieAndDocumentToCompareRef.current = sendSelfieAndDocumentToCompareWrapped;
  }, [sendSelfieAndDocumentToCompareWrapped]);

  return {
    sendSelfieAndDocumentToCompare: sendSelfieAndDocumentToCompareWrapped,
    sendSelfieToDetectFace: sendAndVerifySelfieForFaceDetectionWrapped,
    sendAndReadImageForOCR: sendAndReadImageForOCRWrapped,
    sendFrontSideDocument: sendFrontSideDocumentWrapped,
    sendBackSideDocument: sendBackSideDocumentWrapped,
    isProcessing: isProcessModalOpen,
    processState,
    ProcessModal,
    ErrorModal,
    RetryModal,
  };
}

export {useDocumentSelfieDetection};
